/* eslint-disable no-param-reassign */
import { useState, useEffect, useMemo, createRef } from "react";
import axios from "axios";
import { trim, camelCase } from "lodash";
import moment from "moment";
import { useDebounce, useLocalStorage } from "@shared/hookHelpers";
import { responseDataToCamelCase } from "@shared/v2/caseTransformingAxios";
import { markAllAsRead, markAsRead, searchActivities, getUnreadCategoryCountByUsers } from "../api";
import { useCurrentUser, useFeatureFlags, useIsAdmin } from "../../../../reducers/layoutReducer/selectors";
import {
  includeFeatureFlaggedKeys,
  BATCH_1_ACTIVITIES,
  ACTIVITIES_ADDRESS_LIST_FF,
  ACTIVITIES_FEED_TEST_FF,
} from "../shared";

const INITIAL_CATEGORY_COUNT = {
  all: 0,
  ai: 0,
  text: 0,
  requests: 0,
  web: 0,
  crm: 0,
  recruiter: 0,
  marketer: 0,
};

const formatDate = (dateString) => {
  const date = moment(dateString, "MM/DD/YYYY");

  if (date.isSame(moment(), "day")) return "Today";
  if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";

  return date.format("MM/DD/YY");
};

const groupAndSortByDate = (data) => {
  if (data.length === 0) return [];
  const groupedData = data.reduce((acc, activity) => {
    if (!activity) return acc;

    const dateKey = moment.utc(activity.createdAt).local().format("MM/DD/YYYY");

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }

    acc[dateKey].push(activity);

    return acc;
  }, {});

  Object.keys(groupedData).forEach((date) => {
    groupedData[date].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  });

  const sortedArray = Object.entries(groupedData)
    .sort((a, b) => new Date(b[0]) - new Date(a[0]))
    .map(([date, activities]) => ({ date: formatDate(date), activities }));

  return sortedArray;
};

const transformNotificationData = (notification) => ({
  ...notification,
  data:
    typeof notification.data === "string"
      ? responseDataToCamelCase(JSON.parse(notification.data || "{}"))
      : notification.data,
});

const LIMIT = 30;

const useActivities = ({ show, activitiesRef, agents }) => {
  const isAdmin = useIsAdmin();
  const [query, setQuery] = useLocalStorage("query", "");
  const debouncedQuery = useDebounce(trim(query), 300);
  const [category, setCategory] = useLocalStorage("category", "all");
  const [recipientUuids, setRecipientUuids] = useLocalStorage("recipientLocalUuids", []);
  const [countData, setCountData] = useLocalStorage("countLocalData", 30);
  const [page, setPage] = useLocalStorage("page", 1);
  const [maxPage, setMaxPage] = useLocalStorage("maxPage", 1);
  const [scrolledTop, setScrolledTop] = useLocalStorage("scrolledTop", 0);

  const featureFlag = useFeatureFlags();

  const NOTIFICATION_CATEGORY = useMemo(
    () =>
      Object.entries({
        ...BATCH_1_ACTIVITIES,
        ...includeFeatureFlaggedKeys(featureFlag.addressListStatus, ACTIVITIES_ADDRESS_LIST_FF),
        ...includeFeatureFlaggedKeys(featureFlag.activityFeedTest, ACTIVITIES_FEED_TEST_FF),
      }).reduce((acc, [key, value]) => {
        const camelKey = camelCase(key);
        acc[camelKey] = value.category;
        return acc;
      }, {}),
    [featureFlag.addressListStatus, featureFlag.activityFeedTest],
  );

  const calculateTotalUnreadCount = (data) =>
    Object.keys(NOTIFICATION_CATEGORY).reduce((acc, curr) => (data[curr] ? acc + data[curr] : acc), 0);

  const [categoryCount, setCategoryCount] = useState(INITIAL_CATEGORY_COUNT);
  const [unreadCount, setUnreadCount] = useState(0);

  const [data, setData] = useState([]);
  const [sortedActivities, setSortedActivities] = useState(groupAndSortByDate(data));
  const [loading, setLoading] = useState(false);

  const [readFilter, setReadFilter] = useState(() => {
    const storedValue = localStorage.getItem("readFilter");
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const setReadFilterHandlr = (valueOrUpdater) => {
    const newValue = typeof valueOrUpdater === "function" ? valueOrUpdater(readFilter) : valueOrUpdater;
    setReadFilter(newValue);
    localStorage.setItem("readFilter", JSON.stringify(newValue));
  };

  const { uuid: currentUserUuid } = useCurrentUser();

  const adaptedRecipientUuids = recipientUuids?.filter((uuid) => uuid !== "all").join(",");

  const getUnreadCategoryCountByUsersHandlr = () =>
    getUnreadCategoryCountByUsers({
      params: {
        recipient_uuids:
          recipientUuids[0] !== "all"
            ? adaptedRecipientUuids
            : agents
                .map((agent) => agent.value)
                .filter((value) => value !== "all")
                .join(","),
      },
    }).then((d) => {
      const categoryData = Object.keys(NOTIFICATION_CATEGORY).reduce(
        (acc, curr) => {
          const count = d[curr] || 0;
          return {
            ...acc,
            [NOTIFICATION_CATEGORY[curr]]: count + acc[NOTIFICATION_CATEGORY[curr]],
          };
        },
        {
          ai: 0,
          recruiter: 0,
          marketer: 0,
          text: 0,
          requests: 0,
          web: 0,
          crm: 0,
        },
      );

      setCategoryCount({
        ...categoryData,
        all: calculateTotalUnreadCount(d),
      });
    });

  const resetData = (abortSignal, paramsOptions) => {
    setLoading(true);
    searchActivities({
      params: {
        page: 1,
        limit: LIMIT,
        read: readFilter,
        recipient_uuids: adaptedRecipientUuids,
        category: category === "all" ? null : category,
        term: query,
        ...paramsOptions,
      },
      signal: abortSignal,
    }).then((d) => {
      setLoading(false);

      // this options is used on initial show, so it prevents page count to fetch unnecessarily
      if (!paramsOptions?.limit) {
        setPage(1);
        if (d?.length >= LIMIT) setMaxPage(page + 1);
      }
      setData((d || []).map(transformNotificationData));
    });

    if (agents.length) {
      getUnreadCategoryCountByUsersHandlr();
    }
  };

  useEffect(() => {
    getUnreadCategoryCountByUsers({
      params: {
        recipient_uuids: currentUserUuid,
      },
    }).then((d) => {
      const totalUnreadCount = calculateTotalUnreadCount(d);

      setUnreadCount(totalUnreadCount);
    });

    if (agents.length) {
      const agentsSet = new Set(agents.map((agent) => agent.value));

      if (!recipientUuids.length || !recipientUuids.every((id) => agentsSet.has(id)) || !isAdmin) {
        setRecipientUuids([currentUserUuid]);
      }

      getUnreadCategoryCountByUsersHandlr();
    }
  }, [agents]);

  useEffect(() => {
    const refedData = data.map((d) => ({ ...d, ref: createRef(null) }));
    setSortedActivities(
      groupAndSortByDate(refedData).map((group) => ({ ...group, dateRef: createRef(null) })),
    );
    setLoading(false);
  }, [data]);

  useEffect(() => {
    if (page > 1 && show) {
      setLoading(true);
      searchActivities({
        params: {
          term: debouncedQuery,
          page,
          limit: LIMIT,
          read: readFilter,
          recipient_uuids: adaptedRecipientUuids,
          category: category === "all" ? null : category,
        },
      })
        .then((d) => {
          if (d.length >= LIMIT) {
            setMaxPage(page + 1);
          }
          setData((prev) => {
            const newData = [...prev, ...(d || []).map(transformNotificationData)];
            setCountData(newData.length);
            return newData;
          });
        })
        .catch(() => setLoading(false));
    }
  }, [page]);

  useEffect(() => {
    const abortController = new AbortController();

    if (show) {
      setScrolledTop(0);
      resetData(abortController.signal, {});
    }
    return () => abortController.abort();
  }, [readFilter, category, recipientUuids]);

  useEffect(() => {
    if (activitiesRef?.current) {
      activitiesRef.current.scrollTop = scrolledTop;
    }
  }, [sortedActivities]);

  useEffect(() => {
    if (!show) return () => {};
    const abortController = new AbortController();
    if (show) {
      resetData(abortController.signal, {
        limit: Number(countData) || LIMIT,
      });
    }
    return () => abortController.abort();
  }, [show]);

  useEffect(() => {
    if (!debouncedQuery && show) {
      resetData();
      return () => {};
    }
    const abortController = new AbortController();
    setLoading(true);
    if (debouncedQuery && show) {
      searchActivities({
        params: {
          term: debouncedQuery,
          page: 1,
          limit: LIMIT,
          read: readFilter,
          recipient_uuids: adaptedRecipientUuids,
          category: category === "all" ? null : category,
        },
        signal: abortController.signal,
      })
        .then((d) => {
          setPage(1);
          setMaxPage(d && d.length >= LIMIT ? 2 : 1);
          setData(() => {
            const newData = (d || []).map(transformNotificationData);
            setCountData(newData.length);

            return newData;
          });
        })
        .catch((err) => {
          if (!axios.isCancel(err)) setLoading(false);
        });
    }
    return () => {
      abortController.abort();
    };
  }, [debouncedQuery]);

  const actions = {
    onReadChange: (activity) => {
      markAsRead(activity).then(() => {
        const newReadCount = activity.read ? 1 : -1;
        const updatedCategory = NOTIFICATION_CATEGORY[camelCase(activity.notificationType)];

        setUnreadCount((c) => c + newReadCount);
        setData((prev) => {
          const newData = prev.filter((a) => a.id !== activity.id);
          setCountData(newData.length);
          return newData;
        });

        setCategoryCount((prev) => ({
          ...prev,
          all: prev.all + newReadCount,
          [updatedCategory]: prev[updatedCategory] + newReadCount,
        }));
      });
    },

    markAllAsRead: () => {
      setLoading(true);
      markAllAsRead({
        recipient_uuids:
          recipientUuids[0] !== "all"
            ? adaptedRecipientUuids
            : agents
                .map((agent) => agent.value)
                .filter((value) => value !== "all")
                .join(","),
      })
        .then(() => {
          setUnreadCount(0);
          setCountData(LIMIT);
          setData([]);
          setCategoryCount(INITIAL_CATEGORY_COUNT);
        })
        .catch(() => setLoading(false));
    },

    loadMore: () => {
      if (data.length >= 30 && page < maxPage) setPage((p) => p + 1);
    },

    toggleReadFilter: () => {
      setReadFilterHandlr((prev) => !prev);
    },

    setCategory: (ct) => {
      setCategory(ct);
    },
    setRecipientUuids: (arrayOfUuids) => {
      setRecipientUuids(arrayOfUuids);
    },
    setScrolledTop: (value) => {
      setScrolledTop(value);
    },
  };

  return {
    actions,
    activityGroups: sortedActivities,
    selectedAgentsIds: recipientUuids,
    categoryCount,
    loading,
    unreadCount,
    readFilter,
    category,
    query, // returning query in case it's needed externally
    setQuery, // allows external updates to the query value
  };
};

export default useActivities;
